import React, { useState, useEffect } from 'react';
import { Mail, ArrowRight, Star, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { businessInfo, testimonials, aboutInfo } from '../mockData';
import { categoryAPI } from '../api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [email, setEmail] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsVisible(true);
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryAPI.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // If API fails, show empty categories
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast({
        title: "Welcome to our community!",
        description: "You've been added to our newsletter.",
      });
      setEmail('');
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    toast({
      title: category.category,
      description: `Explore our ${category.count} ${category.category.toLowerCase()} products`,
    });
  };

  return (
    <div className="home-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <Leaf className="logo-icon" />
            <span className="logo-text">{businessInfo.name}</span>
          </div>
          <nav className="nav-links">
            <a href="#products">Products</a>
            <a href="#about">About</a>
            <a href="#testimonials">Testimonials</a>
            <a href="#contact">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className={`hero-section ${isVisible ? 'visible' : ''}`}>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">{businessInfo.name}</h1>
          <p className="hero-tagline">{businessInfo.tagline}</p>
          <p className="hero-description">{businessInfo.description}</p>
          <div className="hero-cta">
            <Button size="lg" className="cta-primary">
              Explore Collection <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
        <div className="hero-image-container">
          <img 
            src="https://images.unsplash.com/photo-1747451050504-3b268d62c3da" 
            alt="Luxury handcrafted products"
            className="hero-image"
          />
        </div>
      </section>

      {/* Featured Categories */}
      <section id="products" className="categories-section">
        <div className="section-header">
          <h2 className="section-title">Our Collections</h2>
          <p className="section-subtitle">Discover handcrafted treasures across our curated categories</p>
        </div>
        {loading ? (
          <div className="loading-state">
            <p>Loading collections...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="empty-categories">
            <p>No products available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="categories-grid">
            {categories.map((category, index) => {
              // Get the first product image or use a default
              const imageUrl = category.products && category.products.length > 0 && category.products[0].images && category.products[0].images.length > 0
                ? `${BACKEND_URL}${category.products[0].images[0]}`
                : 'https://images.unsplash.com/photo-1599945470646-509f49113e9f';
              
              return (
                <Card 
                  key={index} 
                  className="category-card"
                  onClick={() => handleCategoryClick(category)}
                >
                  <div className="category-image-wrapper">
                    <img 
                      src={imageUrl} 
                      alt={category.category}
                      className="category-image"
                    />
                    <div className="category-overlay"></div>
                  </div>
                  <CardContent className="category-content">
                    <h3 className="category-name">{category.category}</h3>
                    <p className="category-description">
                      {category.count > 0 
                        ? `Discover our collection of handcrafted ${category.category.toLowerCase()}` 
                        : 'Coming soon'}
                    </p>
                    <div className="category-footer">
                      <span className="product-count">{category.count} Products</span>
                      <Button variant="ghost" className="view-more-btn">
                        View More <ArrowRight size={16} className="ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="about-container">
          <div className="about-image-side">
            <img 
              src="https://images.unsplash.com/photo-1599945470646-509f49113e9f" 
              alt="Natural botanical elements"
              className="about-image"
            />
          </div>
          <div className="about-content-side">
            <h2 className="about-title">{aboutInfo.title}</h2>
            <p className="about-text">{aboutInfo.content}</p>
            <div className="values-grid">
              {aboutInfo.values.map((value, index) => (
                <div key={index} className="value-item">
                  <div className="value-icon">
                    <Leaf size={24} />
                  </div>
                  <div>
                    <h4 className="value-title">{value.title}</h4>
                    <p className="value-description">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-header">
          <h2 className="section-title">What Our Customers Say</h2>
          <p className="section-subtitle">Real stories from our community</p>
        </div>
        <div className="testimonials-grid">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.id} className="testimonial-card">
              <CardContent className="testimonial-content">
                <div className="stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="currentColor" />
                  ))}
                </div>
                <p className="testimonial-comment">"{testimonial.comment}"</p>
                <div className="testimonial-author">
                  <p className="author-name">{testimonial.name}</p>
                  <p className="author-location">{testimonial.location}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact & Newsletter */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-info-side">
            <h2 className="contact-title">Get in Touch</h2>
            <p className="contact-description">
              Have questions or want to place a custom order? We'd love to hear from you!
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <Mail size={24} />
                <a href={`mailto:${businessInfo.email}`}>{businessInfo.email}</a>
              </div>
            </div>
          </div>
          <div className="newsletter-side">
            <Card className="newsletter-card">
              <CardContent className="newsletter-content">
                <h3 className="newsletter-title">Join Our Newsletter</h3>
                <p className="newsletter-description">
                  Stay updated with new products, exclusive offers, and artisan tips
                </p>
                <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="newsletter-input"
                  />
                  <Button type="submit" className="newsletter-btn">
                    Subscribe
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <Leaf size={28} />
            <span>{businessInfo.name}</span>
          </div>
          <p className="footer-text">Handcrafted with love, inspired by nature</p>
          <p className="footer-copyright">© 2025 The LaShay & Silk Collective. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
